import {
  Crown,
  Users,
  Shield,
  Zap,
  Building,
  Vote,
  AlertTriangle,
  CheckCircle,
} from 'lucide-react';

export type GlossaryCategory = 'roles' | 'processes' | 'concepts' | 'comparison';

export interface GlossaryTerm {
  id: string;
  term: string;
  category: GlossaryCategory;
  shortDefinition: string;
  fullDefinition: string;
  examples: string[];
  relatedTerms: string[];
  icon: React.ComponentType<{ className?: string; size?: number }>; // lucide-react icon component
  traditionalEquivalent?: string;
}

export const GOVERNANCE_GLOSSARY_TERMS: GlossaryTerm[] = [
  {
    id: 'sp-elect',
    term: 'SP-Elect',
    category: 'roles',
    shortDefinition: 'The final decision maker who stays above day-to-day politics',
    fullDefinition:
      "Think of this as your community's \"wise judge\" - they don't get involved in daily decisions, but when there's a serious disagreement or emergency, they step in to make the final call. They're elected by the community to be fair and impartial.",
    examples: [
      "If directors can't agree on a major budget item, the SP-Elect makes the final decision",
      'During emergencies, the SP-Elect can authorize immediate actions',
      "Residents can petition the SP-Elect to override a director's decision",
    ],
    relatedTerms: ['sp-director', 'override-authority', 'community-subpanel'],
    icon: Crown,
    traditionalEquivalent: 'Board President (but stays neutral instead of being political)',
  },
  {
    id: 'sp-director',
    term: 'SP-Director',
    category: 'roles',
    shortDefinition: 'The people who handle day-to-day community decisions and operations',
    fullDefinition:
      'These are your community\'s "working managers" - usually 1-3 people who make the everyday decisions that keep your community running. They propose solutions, manage budgets, and handle resident concerns.',
    examples: [
      'SP-Director for Operations handles maintenance and repairs',
      'SP-Director for Resources manages the budget and contracts',
      'SP-Director for Community organizes events and communications',
    ],
    relatedTerms: ['sp-elect', 'community-subpanel', 'department-subpanel'],
    icon: Users,
    traditionalEquivalent: 'Board Directors/Committee Chairs (but more clearly defined roles)',
  },
  {
    id: 'community-subpanel',
    term: 'Community Subpanel',
    category: 'concepts',
    shortDefinition: "Your community's main leadership team",
    fullDefinition:
      "This is the core group that governs your community - one SP-Elect and up to three SP-Directors. It's like having a small, efficient city council that actually gets things done quickly.",
    examples: [
      'Makes decisions about community-wide policies',
      'Manages the overall budget and major expenditures',
      'Represents the community in legal and business matters',
    ],
    relatedTerms: ['sp-elect', 'sp-director', 'department-subpanel'],
    icon: Building,
    traditionalEquivalent: 'HOA Board (but smaller and more efficient)',
  },
  {
    id: 'department-subpanel',
    term: 'Department Subpanel',
    category: 'concepts',
    shortDefinition: 'Specialized teams for specific areas like security or maintenance',
    fullDefinition:
      'These are smaller teams that focus on specific aspects of community life - like security, landscaping, or social activities. Each department has the same structure: one leader (Department SP-Elect) and helpers (Department SP-Directors).',
    examples: [
      'Security Department manages safety and access control',
      'Maintenance Department handles repairs and upkeep',
      'Social Department organizes community events',
    ],
    relatedTerms: ['community-subpanel', 'sp-elect', 'sp-director'],
    icon: Shield,
    traditionalEquivalent: 'Committees (but with clear leadership and decision-making power)',
  },
  {
    id: 'override-authority',
    term: 'Override Authority',
    category: 'concepts',
    shortDefinition: "The power to make final decisions when there's disagreement",
    fullDefinition:
      'This is like having a "tie-breaker" or "appeals court" for your community. When directors disagree or residents are unhappy with a decision, someone with override authority can step in and make the final call.',
    examples: [
      'Community votes to ask SP-Elect to override a budget decision',
      'Emergency situations where normal voting would be too slow',
      'Resolving conflicts between different departments',
    ],
    relatedTerms: ['sp-elect', 'community-override', 'emergency-bypass'],
    icon: Zap,
    traditionalEquivalent: 'Board President authority (but used more rarely and fairly)',
  },
  {
    id: 'community-override',
    term: 'Community Override',
    category: 'processes',
    shortDefinition: "Residents' power to change or reverse leadership decisions",
    fullDefinition:
      "This gives YOU, as a resident, real power. If you don't like what your leadership is doing, you can organize other residents to override their decisions or even remove them from office through digital voting.",
    examples: [
      'Petition to reverse a controversial rule change',
      'Vote to remove an underperforming SP-Director',
      'Request SP-Elect review of a major spending decision',
    ],
    relatedTerms: ['digital-voting', 'override-authority', 'voting-eligibility'],
    icon: Vote,
    traditionalEquivalent: 'Resident petitions/recalls (but much easier and faster)',
  },
  {
    id: 'digital-governance',
    term: 'Digital Governance',
    category: 'concepts',
    shortDefinition: 'Using technology to make community decisions faster and more transparent',
    fullDefinition:
      'Instead of monthly meetings and paper ballots, everything happens online in real-time. You can vote on your phone, see exactly how decisions are made, and track what your leaders are doing 24/7.',
    examples: [
      'Vote on community issues from your smartphone',
      'See real-time budget spending and decision tracking',
      'Get instant notifications about issues affecting your property',
    ],
    relatedTerms: ['community-override', 'voting-eligibility', 'transparency'],
    icon: Zap,
    traditionalEquivalent: 'Traditional HOA meetings (but online and much more efficient)',
  },
  {
    id: 'voting-eligibility',
    term: 'Voting Eligibility',
    category: 'processes',
    shortDefinition: 'Who can vote on what decisions in your community',
    fullDefinition:
      'Clear rules about which residents can vote on which issues, based on property ownership, residency status, and the type of decision being made. No more confusion about who has a voice.',
    examples: [
      'Property owners vote on budget and major policy changes',
      'All residents vote on community events and amenities',
      'Affected properties vote on local improvements like street repairs',
    ],
    relatedTerms: ['digital-voting', 'community-override', 'stakeholder-verification'],
    icon: CheckCircle,
    traditionalEquivalent: 'Voting rights rules (but clearer and automatically enforced)',
  },
  {
    id: 'emergency-bypass',
    term: 'Emergency Bypass',
    category: 'processes',
    shortDefinition: 'Fast-track decision making for urgent safety or infrastructure issues',
    fullDefinition:
      "When there's an emergency like a water main break or security threat, leaders can make immediate decisions without waiting for normal voting processes. But these decisions are still tracked and reviewed.",
    examples: [
      'Road washout blocking emergency access gets immediate repair approval',
      'Security breach triggers immediate protocol changes',
      'Water system failure gets emergency contractor authorization',
    ],
    relatedTerms: ['override-authority', 'sp-elect', 'community-oversight'],
    icon: AlertTriangle,
    traditionalEquivalent: 'Emergency board powers (but with better oversight and transparency)',
  },
  {
    id: 'legacy-hoa',
    term: 'Legacy HOA',
    category: 'comparison',
    shortDefinition: "Traditional homeowner association governance that's slow and frustrating",
    fullDefinition:
      'The old way of running communities: monthly meetings, unclear roles, political infighting, and residents who feel powerless. Decisions take months, transparency is limited, and accountability is poor.',
    examples: [
      'Board meetings where nothing gets decided',
      'Months of delays for simple maintenance approvals',
      'Residents frustrated by lack of transparency in spending',
    ],
    relatedTerms: ['digital-governance', 'community-subpanel', 'transparency'],
    icon: AlertTriangle,
    traditionalEquivalent: 'What most communities are stuck with now',
  },
  {
    id: 'stakeholder-verification',
    term: 'Stakeholder Verification',
    category: 'processes',
    shortDefinition: 'Confirming who owns what property and can vote on community decisions',
    fullDefinition:
      'A digital system that automatically verifies property ownership and voting rights, eliminating confusion about who can participate in community governance. No more arguments about voting eligibility.',
    examples: [
      'Property ownership records linked to voting accounts',
      'Automatic verification when new owners move in',
      'Clear records of voting eligibility for each decision type',
    ],
    relatedTerms: ['voting-eligibility', 'digital-governance', 'community-override'],
    icon: CheckCircle,
    traditionalEquivalent: 'Manual verification of homeowner status (but automated and accurate)',
  },
];

export const GLOSSARY_CATEGORIES = [
  { id: 'all', label: 'All Terms' },
  { id: 'roles', label: 'Leadership Roles' },
  { id: 'concepts', label: 'Key Concepts' },
  { id: 'processes', label: 'How It Works' },
  { id: 'comparison', label: 'vs Traditional' },
] as const;
